import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import {
  NodeApiModel,
  NodeRelationship,
  WithBreaklineLayer,
  WithDomains,
  WithFeatureType,
  WithPointLayer,
  WithSymbol,
} from '../../../../types/models/node.model';
import {
  TrimbleLayerApiModel,
  TrimbleLayerRelationship,
  WithLayer,
} from '../../../../types/models/trimble-layer.model';
import {
  useAddDomainToNode,
  useCreateNode,
  useGetNode,
  useRemoveDomainFromNode,
  useUpdateNode,
} from '../../hooks/api/use-ontology.hook';
import { NodeType } from '../../../../types/constants/node-type';
import { LoadingIndicator } from '../LoadingIndicator';
import { useSearchDomains } from '../../hooks/api/use-domains.hook';
import { useSearchSymbols } from '../../hooks/api/use-symbols.hook';
import { useSearchFeatureTypes } from '../../hooks/api/use-feature-types.hook';
import { useSearchTrimbleLayers } from '../../hooks/api/use-trimble-layers.hook';
import { SurveyCodeForm } from './SurveyCodeForm';
import { getDirtyValues } from '../../utils/react-hook-form';

export const AddEditSurveyCode = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { nodeId } = useParams<{ nodeId: string }>();

  const {
    node,
    loading: loadingNode,
    error: gettingError,
  } = useGetNode<NodeApiModel & WithDomains & WithPointLayer & WithBreaklineLayer & WithSymbol & WithFeatureType>({
    nodeId: location.pathname.endsWith('/edit') && nodeId ? +nodeId : Number.NaN,
    nodeType: NodeType.MASTER,
    include: [
      NodeRelationship.DOMAINS,
      NodeRelationship.POINT_LAYER,
      NodeRelationship.BREAKLINE_LAYER,
      NodeRelationship.SYMBOL,
      NodeRelationship.FEATURE_TYPES,
    ],
  });
  const [createNode, { loading: creatingNode, error: createError }] = useCreateNode();
  const [updateNode, { loading: updatingNode, error: updateError }] = useUpdateNode();
  const [assignDomainToNode] = useAddDomainToNode();
  const [removeDomainFromNode] = useRemoveDomainFromNode();
  const { loading: loadingDomains, response: domainsResponse, error: domainsError } = useSearchDomains({});
  const {
    loading: loadingLayers,
    response: layersResponse,
    error: layersError,
  } = useSearchTrimbleLayers<TrimbleLayerApiModel & WithLayer>({ include: [TrimbleLayerRelationship.LAYER] });
  const { loading: loadingSymbols, response: symbolsResponse, error: symbolsError } = useSearchSymbols({});
  const {
    loading: loadingFeatureTypes,
    response: featureTypesResponse,
    error: featureTypesError,
  } = useSearchFeatureTypes({});

  const {
    control,
    register,
    watch,
    handleSubmit,
    formState: { errors, dirtyFields, isSubmitting },
  } = useForm<SurveyCodeForm>({ resolver: classValidatorResolver(SurveyCodeForm), values: node });
  const currentFeatureTypeValue = watch('featureType');

  const onSubmit: SubmitHandler<SurveyCodeForm> = async (submittedValues) => {
    if (!node) {
      const { symbol, pointLayer, breaklineLayer, featureType, domains, ...values } = submittedValues;
      await createNode({
        ...values,
        attachments: [],
        parentId: nodeId ? +nodeId : undefined,
        type: NodeType.MASTER,
        featureTypeId: featureType.id,
        pointLayerId: pointLayer?.id,
        breaklineLayerId: breaklineLayer?.id,
        symbolId: symbol?.id,
        domainIds: domains.map((domain) => domain.id),
      });
    } else {
      const { symbol, pointLayer, breaklineLayer, featureType, domains, ...values } = getDirtyValues(
        dirtyFields,
        submittedValues,
      );
      const domainsToAdd = domains?.filter(({ id }) => !node.domains.find((domain) => domain.id === id)) ?? [];
      const domainsToRemove = domains
        ? node.domains.filter(({ id }) => !domains?.find((domain) => domain.id === id))
        : [];

      await Promise.all([
        updateNode({
          ...values,
          parentId: node.parent?.id,
          nodeId: node.id,
          nodeType: node.type,
          featureTypeId: featureType?.id,
          pointLayerId: pointLayer?.id,
          breaklineLayerId: breaklineLayer?.id,
          symbolId: symbol?.id,
        }),
        ...domainsToAdd.map(({ id: domainId }) => assignDomainToNode({ nodeId: node.id, domainId })),
        ...domainsToRemove.map(({ id: domainId }) => removeDomainFromNode({ nodeId: node.id, domainId })),
      ]);
    }

    navigate('..');
  };

  const renderContent = () => {
    const error = gettingError || domainsError || layersError || symbolsError || featureTypesError;
    if (error) {
      return <Alert severity="error">{error?.response?.data.message}</Alert>;
    }

    return (
      <>
        <TextField
          {...register('name')}
          variant="outlined"
          sx={{ mt: '1rem' }}
          disabled={creatingNode || updatingNode}
          error={Boolean(errors.name)}
          helperText={errors.name?.message}
          label="Name"
          margin="dense"
          size="small"
          fullWidth
        />
        <TextField
          {...register('description')}
          variant="outlined"
          sx={{ mt: '1rem' }}
          disabled={creatingNode || updatingNode}
          error={Boolean(errors.description)}
          helperText={errors.description?.message}
          label="Description"
          margin="dense"
          size="small"
          fullWidth
        />
        <FormControlLabel
          sx={{ ml: 0, mt: 0 }}
          label="Include in Surface:"
          labelPlacement="start"
          control={
            <Controller
              control={control}
              name="includeInSurface"
              render={({ field }) => <Checkbox {...register('includeInSurface')} checked={Boolean(field.value)} />}
            />
          }
        />
        <Controller
          control={control}
          name="domains"
          render={({ field }) => (
            <Autocomplete
              {...register('domains')}
              loading={loadingDomains}
              sx={{ mt: '1rem' }}
              multiple
              onChange={(_event, selectedOptions) => field.onChange(selectedOptions)}
              onBlur={field.onBlur}
              value={field.value}
              getOptionLabel={(domain) => domain?.shortName ?? ''}
              options={domainsResponse?.pages.map(({ result }) => result).flat() ?? []}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Domains"
                  error={Boolean(errors.domains)}
                  helperText={errors.domains?.message}
                />
              )}
            />
          )}
        />
        <Controller
          control={control}
          name="featureType"
          render={({ field }) => (
            <Autocomplete
              {...register('featureType')}
              loading={loadingFeatureTypes}
              sx={{ mt: '1rem' }}
              onChange={(_event, selectedOptions) => field.onChange(selectedOptions)}
              onBlur={field.onBlur}
              value={field.value}
              getOptionLabel={({ name }) => name ?? ''}
              options={featureTypesResponse?.pages.flatMap(({ result }) => result) ?? []}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Feature Type"
                  error={Boolean(errors.featureType)}
                  helperText={errors.featureType?.message}
                />
              )}
            />
          )}
        />
        <Controller
          control={control}
          name="pointLayer"
          render={({ field }) => (
            <Autocomplete
              {...register('pointLayer', { setValueAs: (value) => value || null })}
              loading={loadingLayers}
              sx={{ mt: '1rem' }}
              onChange={(_event, selectedOptions) => field.onChange(selectedOptions)}
              onBlur={field.onBlur}
              value={field.value}
              getOptionLabel={({ name }) => name ?? ''}
              options={layersResponse?.pages.flatMap(({ result }) => result.map(({ layer }) => layer)) ?? []}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Point Layer"
                  error={Boolean(errors.pointLayer)}
                  helperText={errors.pointLayer?.message}
                />
              )}
            />
          )}
        />
        {currentFeatureTypeValue?.name !== 'Point' ? (
          <Controller
            control={control}
            name="breaklineLayer"
            render={({ field }) => (
              <Autocomplete
                {...register('breaklineLayer', { setValueAs: (value) => value || null })}
                loading={loadingLayers}
                sx={{ mt: '1rem' }}
                onChange={(_event, selectedOptions) => field.onChange(selectedOptions)}
                onBlur={field.onBlur}
                value={field.value}
                getOptionLabel={({ name }) => name ?? ''}
                options={layersResponse?.pages.flatMap(({ result }) => result.map(({ layer }) => layer)) ?? []}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Linework Layer"
                    error={Boolean(errors.breaklineLayer)}
                    helperText={errors.breaklineLayer?.message}
                  />
                )}
              />
            )}
          />
        ) : null}
        <Controller
          control={control}
          name="symbol"
          render={({ field }) => (
            <Autocomplete
              {...register('symbol', { setValueAs: (value) => value || null })}
              loading={loadingSymbols}
              sx={{ mt: '1rem' }}
              onChange={(_event, selectedOptions) => field.onChange(selectedOptions)}
              onBlur={field.onBlur}
              value={field.value}
              getOptionLabel={({ name }) => name ?? ''}
              options={symbolsResponse?.pages.map(({ result }) => result).flat() ?? []}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Symbol"
                  error={Boolean(errors.symbol)}
                  helperText={errors.symbol?.message}
                />
              )}
            />
          )}
        />
      </>
    );
  };

  if (loadingNode) {
    // FIXME: workaround for react-use-form not resetting controlled fields in a dialog
    return <LoadingIndicator />;
  }

  return (
    <Dialog open={true}>
      {loadingNode ? (
        <LoadingIndicator />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>{`${node ? 'Edit' : 'Add'} ${NodeType.MASTER}`}</DialogTitle>
          <DialogContent>
            {renderContent()}
            {createError || updateError ? (
              <Alert sx={{ mt: '1rem' }} severity="error">
                {createError?.response?.data?.message || updateError?.response?.data?.message}
              </Alert>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button disabled={isSubmitting} variant="outlined" onClick={() => navigate('..')} color="primary">
              Cancel
            </Button>
            <LoadingButton
              color="primary"
              type="submit"
              variant="contained"
              loading={isSubmitting || Boolean(gettingError)}
            >
              {node ? 'Save' : 'Create'}
            </LoadingButton>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
};
